import { formatHotelOrderStayRange, resolveHotelOrderStay } from '@utils/format-hotel-stay'

export function normalizeNumber(value) {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

export function normalizeQuantity(item = {}) {
  const qty = Number(item.qty != null ? item.qty : item.quantity)
  return Number.isFinite(qty) && qty > 0 ? qty : 1
}

export function resolveAddonUnitPrice(item = {}, orderNights = 1) {
  const billingType = item.billingType != null ? item.billingType : item.billing_type
  const nights = Math.max(1, Number(orderNights) || 1)

  if (item.adjustPrice != null) return normalizeNumber(item.adjustPrice)
  if (item.unitPrice != null) return normalizeNumber(item.unitPrice)

  if (item.addonPrice != null) {
    const n = normalizeNumber(item.addonPrice)
    return billingType === 'per_night' ? n / nights : n
  }

  if (item.amount != null) return normalizeNumber(item.amount)
  if (item.price != null) return normalizeNumber(item.price)

  const qty = normalizeQuantity(item)
  if (item.lineTotal != null && qty > 0) {
    const perQty = normalizeNumber(item.lineTotal) / qty
    return billingType === 'per_night' ? perQty / nights : perQty
  }
  return 0
}

export function getOrderPetNameMap(order = {}) {
  const guests = Array.isArray(order.guests) ? order.guests : []
  return guests.reduce((acc, pet) => {
    if (pet && pet.id != null) acc[pet.id] = pet.name || ''
    return acc
  }, {})
}

export function buildOrderBookings(order) {
  const bookings = order && Array.isArray(order.bookings) ? order.bookings : []
  return bookings.map((booking) => ({
    date: booking.date || '',
    roomName: booking.title || '',
    quantity: booking.qty != null ? booking.qty : booking.quantity != null ? booking.quantity : 1,
    price: normalizeNumber(booking.price),
  }))
}

export function buildOrderSummary(order, showCheckoutDate = 1) {
  if (!order) return {}
  const bookings = Array.isArray(order.bookings) ? order.bookings : []
  const roomNames = bookings.map((booking) => booking.title).filter(Boolean)
  const uniqueNames = Array.from(new Set(roomNames))
  let roomTitle = ''
  if (uniqueNames.length <= 2) {
    roomTitle = uniqueNames.join('＆')
  } else {
    roomTitle = uniqueNames.slice(0, 2).join('＆') + '...'
  }
  const stay = resolveHotelOrderStay(order, showCheckoutDate)
  return {
    roomTitle,
    dateRange: formatHotelOrderStayRange(order, showCheckoutDate),
    nights: stay?.nights ?? null,
  }
}

export function buildOrderAdjustments(order, stayNights) {
  if (!order) return []
  const petNameMap = getOrderPetNameMap(order)
  const orderNights = Math.max(1, Number(stayNights) || Number(order.nights) || 1)
  const toPriceAdjustment = (item) => ({
    itemType: 'price',
    type: item.type,
    reason: item.title || item.reason || '-',
    qty: normalizeQuantity(item),
    amount: normalizeNumber(item.amount != null ? item.amount : item.price),
  })
  const toAddonAdjustment = (item) => {
    const petName = petNameMap[item.petId] || ''
    const addonName = item.goodsName || item.title || item.name || '-'
    const billingType = item.billingType != null ? item.billingType : item.billing_type
    return {
      itemType: 'addon',
      type: 'add',
      reason: item.reason || (petName ? `${petName}｜${addonName}` : addonName),
      qty: normalizeQuantity(item),
      amount: resolveAddonUnitPrice(item, orderNights),
      ...(billingType != null && billingType !== '' ? { billingType } : {}),
    }
  }

  const priceAdjustments = [...(order.adjustments || []), ...(order.customAdjustments || [])].map(toPriceAdjustment)
  const addonAdjustments = [...(order.addonItems || order.addon_items || []), ...(order.addons || [])].map(toAddonAdjustment)

  return [...priceAdjustments, ...addonAdjustments]
}

export function calcOrderSubtotal(bookings, extraGuestAdjustments) {
  const bookingsAmount = (bookings || []).reduce((sum, booking) => {
    const quantity = booking.quantity != null ? booking.quantity : 1
    return sum + normalizeNumber(booking.price) * quantity
  }, 0)
  const extraGuestsFeeAmount = (extraGuestAdjustments || []).reduce((sum, item) => {
    return sum + normalizeNumber(item.price) * item.count
  }, 0)

  return bookingsAmount + extraGuestsFeeAmount
}

export function calcOrderTotal(subtotal, adjustments, stayNights) {
  const adjustmentTotal = (adjustments || []).reduce((sum, item) => {
    const quantity = normalizeQuantity(item)
    const raw = normalizeNumber(item.amount)
    const amount = Math.abs(raw)
    const type = String(item.type || '').toLowerCase()

    if (item.itemType === 'addon') {
      const nightFactor = item.billingType === 'per_night' ? stayNights : 1
      return sum + amount * quantity * nightFactor
    }
    if (['add', 'plus'].includes(type)) return sum + amount * quantity
    if (['minus', 'discount', 'reduce', 'sub', 'subtract'].includes(type)) return sum - amount * quantity

    return sum + raw * quantity
  }, 0)
  return subtotal + adjustmentTotal
}

export function resolveStayNights(summary, order) {
  const n = Number(summary?.nights)
  if (Number.isFinite(n) && n > 0) return n
  const orderNights = Number(order?.nights)
  return Number.isFinite(orderNights) && orderNights > 0 ? orderNights : 1
}
